import React, { useEffect } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import Papa from 'papaparse';
import { Controller, useForm } from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import { discoPrototypeStore } from 'client/app/apps/disco-prototype/dataStore/dexieStore';

type FormValues = {
  name: string;
  file: FileList;
};

export const CsvUploadForm: React.FC = () => {
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: { name: '', file: undefined! },
  });

  const selectedFiles = watch('file');

  useEffect(() => {
    if (selectedFiles && selectedFiles.length > 0) {
      const filename = selectedFiles[0].name.replace(/\.csv$/i, '');
      setValue('name', filename);
    }
  }, [selectedFiles, setValue]);

  const onSubmit = async (data: FormValues) => {
    if (!data.file || data.file.length === 0) return;

    const rows: Record<string, string>[] = await new Promise((resolve, reject) => {
      Papa.parse<Record<string, string>>(data.file[0], {
        header: true,
        skipEmptyLines: true,
        complete: result => resolve(result.data),
        error: err => reject(err),
      });
    });

    await discoPrototypeStore.addDataset({
      id: uuid(),
      name: data.name,
      rows,
      createdAt: new Date().toDateString(),
    });

    reset();
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <Button variant="contained" component="label" size="small">
        Select CSV File
        <input
          type="file"
          accept=".csv"
          hidden
          {...register('file', {
            required: 'CSV file is required',
            validate: files => files?.length === 1 || 'Please select one file',
          })}
        />
      </Button>
      {errors.file && <FormHelperText error>{errors.file.message}</FormHelperText>}
      {selectedFiles?.length === 1 && (
        <FormHelperText>Selected: {selectedFiles[0].name}</FormHelperText>
      )}

      <Controller
        name="name"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Name"
            fullWidth
            required
            margin="normal"
            error={!!errors.name}
            helperText={errors.name?.message}
          />
        )}
      />

      <Button
        type="submit"
        variant="contained"
        color="secondary"
        disabled={isSubmitting || !selectedFiles?.length}
        size="small"
      >
        {isSubmitting ? 'Uploading...' : 'Upload & Save'}
      </Button>
    </Box>
  );
};
